import { useCallback } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import type { TeamMember } from '../api';
import { getProjectPersistenceId, getProjectRouteId, rememberLastUsedProjectId } from '../projectSelection';
import { orgTrackersPath } from '../routing';
import { TrackerItemByRef } from '../links/TrackerItemByRef';
import { TrackersShell } from '../trackers/TrackersShell';
import { TrackerViewSurface } from '../trackers/TrackerViewSurface';
import { useProjectTrackerSession } from '../trackers/ProjectTrackerSession';
import { DocsSurfaceError } from './DocsRoutes';
import type { OrganizationRouteContext } from './workspaceContext';

interface TrackersRouteContext extends OrganizationRouteContext {
  members: readonly TeamMember[];
  presenceName: string;
}

export function TrackersWorkspaceRoute() {
  const context = useOutletContext<OrganizationRouteContext>();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, trackerType, itemId } = useParams<{
    projectId?: string;
    trackerType?: string;
    itemId?: string;
  }>();
  // The roster, identity, room, and purge-on-denied all live one level up so
  // Docs reads the same room; this route only decides what to render.
  const {
    members,
    rosterFailed: failed,
    rosterResolved: resolved,
    identity,
    presenceName,
    session,
  } = useProjectTrackerSession();
  // `navigate` is a new function after every location change, so nothing
  // downstream may key on its identity; the shell reads it through a ref.
  const navigateTo = useCallback((path: string) => navigate(path), [navigate]);
  const selectProject = useCallback((project: typeof context.team.projects[number]) => {
    rememberLastUsedProjectId(context.team.orgId, getProjectPersistenceId(project));
    navigate(orgTrackersPath(context.team, getProjectRouteId(project)));
  }, [context.team, navigate]);

  if (!context.project) {
    return <DocsSurfaceError kind="no-access" detail="This organization has no project available for Trackers." />;
  }
  const selectedProjectId = getProjectRouteId(context.project);
  if (!projectId || projectId !== selectedProjectId) {
    return <Navigate to={orgTrackersPath(context.team, selectedProjectId)} replace />;
  }
  if (failed) {
    return (
      <DocsSurfaceError
        kind="service-unavailable"
        detail="The organization roster could not be loaded, so this browser cannot say who you are in this team."
      />
    );
  }

  if (resolved && !identity) {
    // The roster answered and this session is not on it. The shell would
    // otherwise wait for an identity that is never coming, which on screen is
    // indistinguishable from a slow connection.
    return (
      <DocsSurfaceError
        kind="removed-member"
        detail="You are not listed as a member of this organization, so its trackers cannot be opened here."
      />
    );
  }
  // The item route has no type segment, so the list beside it reads its type
  // from `?type=`. Absent on a pasted bare link, which correctly opens the item
  // against every tracker in the project.
  const search = new URLSearchParams(location.search);
  const trackerContext: TrackersRouteContext = {
    ...context,
    members,
    presenceName,
  };

  return (
    <TrackersShell
      team={context.team}
      project={context.project}
      teamSession={context.teamSession}
      session={session}
      identity={identity}
      navigate={navigateTo}
      onSelectProject={selectProject}
      trackerType={trackerType ?? search.get('type')}
      viewId={search.get('view')}
      activeItemId={itemId ?? null}
      // Passed as a slot rather than rendered through the outlet: an item is a
      // pane beside the rows, not a page that replaces them, and every tracker
      // route renders the same view surface underneath it.
      detail={itemId ? (
        <TrackerItemByRef
          itemId={itemId}
          members={members}
          teamSession={context.teamSession}
          presenceName={trackerContext.presenceName}
          getTeamJwt={context.getTeamJwt}
        />
      ) : null}
    >
      <Outlet context={trackerContext} />
    </TrackersShell>
  );
}

export function TrackersViewRoute() {
  return <TrackerViewSurface />;
}
